import {DateTime} from "luxon";
import type {EBirdChecklistByFamily, EBirdMyDataSchema} from "ebird-mydata-reader";
import {getMacaulayCatalogNumbers} from "../../../helpers/macaulayLibrary";
import {getPreferredHabitatPrimary} from "../../../helpers/habitat-classification";
import type {EnrichedObservationsByLocation} from "../../../helpers/types";
import {getUniqueSpeciesCount, getYear} from "../helpers";
import {buildHotspotDetailPath} from "../../hotspot-explorer/helpers";
import {getFamilySlug} from "../../../helpers/helpers";
import {ACHIEVEMENT_RARITY_GUIDE, ACHIEVEMENT_SET_DEFINITIONS, ACHIEVEMENT_THEME_LABELS} from "./constants";
import type {
    AchievementDashboard,
    AchievementMilestoneProgress,
    AchievementSetDetail,
    AchievementSetDefinition,
    AchievementUnlockContext,
    AchievementMetricId,
    AchievementMetricSnapshot,
    AchievementMilestoneDefinition,
    AchievementSetSummary,
    AchievementsViewProps,
    EarnedAchievement,
    InProgressAchievement
} from "./types";

const ACHIEVEMENT_SET_DEFINITION_BY_ID = new Map<string, AchievementSetDefinition>(
    ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => [setDefinition.id, setDefinition])
);

const getDateTime = (date?: string, time?: string) => {
    return DateTime.fromISO(`${date ?? ''}T${time || '00:00'}`).toMillis() || 0;
}

const getCountryCode = (observation: EBirdMyDataSchema) => {
    return observation.stateProvince?.split('-')[0] || '';
}

const getCountyKey = (observation: EBirdMyDataSchema) => {
    if (!observation.stateProvince || !observation.county) return '';

    return `${observation.stateProvince}|${observation.county}`;
}

const getSeenFamilyCount = (checklist: EBirdChecklistByFamily[]) => {
    return checklist.filter(family => family.seen && family.speciesList.some(species => !species.isExtinct)).length;
}

const getUniqueHabitatCount = (observationsByLocation: AchievementsViewProps['observationsByLocation']) => {
    return new Set(
        observationsByLocation
            .map(locationGroup => getPreferredHabitatPrimary((locationGroup as EnrichedObservationsByLocation).ecoData))
            .filter(Boolean)
    ).size;
}

const getBigYearSpeciesCount = (rawData: EBirdMyDataSchema[]) => {
    const years = Array.from(new Set(rawData.map(observation => getYear(observation.date)).filter(Boolean)));

    return years.reduce((best, year) => {
        const yearSpecies = getUniqueSpeciesCount(rawData.filter(observation => getYear(observation.date) === year));
        return Math.max(best, yearSpecies);
    }, 0);
}

const getCurrentYearSpeciesCount = (rawData: EBirdMyDataSchema[]) => {
    const currentYear = DateTime.now().year.toString();
    return getUniqueSpeciesCount(rawData.filter(observation => getYear(observation.date) === currentYear));
}

const getPhotoObservationCount = (rawData: EBirdMyDataSchema[]) => {
    return rawData.filter(observation => getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length > 0).length;
}

const getObservationSpeciesKey = (observation: EBirdMyDataSchema) => {
    return observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';
}

const getObservationLocationKey = (observation: EBirdMyDataSchema) => {
    return observation.locationId || observation.location || '';
}

const getSpeciesDetailPathByTaxonomicOrder = (checklist: EBirdChecklistByFamily[]) => {
    const speciesDetailPathByTaxonomicOrder = new Map<number, string>();

    for (const family of checklist) {
        const familySlug = getFamilySlug(family.familyName);

        for (const species of family.speciesList) {
            const detailPath = `/family-explorer/family/${familySlug}/species/${species.speciesCode}`;
            speciesDetailPathByTaxonomicOrder.set(species.taxonomicOrder, detailPath);

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                speciesDetailPathByTaxonomicOrder.set(alternateTaxonomicOrder, detailPath);
            }
        }
    }

    return speciesDetailPathByTaxonomicOrder;
}

const getLocationGroupKey = (locationGroup: AchievementsViewProps['observationsByLocation'][number]) => {
    return locationGroup.locationId || locationGroup.location || '';
}

const hasEcoData = (locationGroup: AchievementsViewProps['observationsByLocation'][number]): locationGroup is EnrichedObservationsByLocation => {
    return 'ecoData' in locationGroup && Boolean(locationGroup.ecoData);
}

const getSortedObservations = (rawData: EBirdMyDataSchema[]) => {
    return [...rawData].sort((left, right) => getDateTime(left.date, left.time) - getDateTime(right.date, right.time));
}

type MilestoneUnlockDetail = {
    date?: string,
    unlockContext?: AchievementUnlockContext
}

const assignMilestoneDatesFromProgress = (
    milestoneDates: Map<string, MilestoneUnlockDetail>,
    milestones: AchievementMilestoneDefinition[],
    progressEvents: Array<{value: number, date?: string, unlockContext?: AchievementUnlockContext}>
) => {
    const sortedMilestones = [...milestones].sort((left, right) => left.threshold - right.threshold);
    let milestoneIndex = 0;

    for (const event of progressEvents) {
        while (milestoneIndex < sortedMilestones.length && event.value >= sortedMilestones[milestoneIndex].threshold) {
            milestoneDates.set(sortedMilestones[milestoneIndex].id, {
                date: event.date,
                unlockContext: event.unlockContext
            });
            milestoneIndex += 1;
        }

        if (milestoneIndex >= sortedMilestones.length) {
            break;
        }
    }
}

const getFamilyBySpeciesKey = (checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = new Map<string, {familyCommon: string, familySlug: string}>();

    for (const family of checklist) {
        const familySlug = getFamilySlug(family.familyName);
        for (const species of family.speciesList) {
            familyBySpeciesKey.set(species.taxonomicOrder.toString(), {
                familyCommon: family.familyCommon,
                familySlug
            });

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                familyBySpeciesKey.set(alternateTaxonomicOrder.toString(), {
                    familyCommon: family.familyCommon,
                    familySlug
                });
            }
        }
    }

    return familyBySpeciesKey;
}

const buildMilestoneUnlockDates = ({rawData, observationsByLocation, checklist}: AchievementsViewProps) => {
    const milestoneDates = new Map<string, MilestoneUnlockDetail>();
    const sortedObservations = getSortedObservations(rawData);
    const setById = new Map(ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => [setDefinition.id, setDefinition]));
    const speciesDetailPathByTaxonomicOrder = getSpeciesDetailPathByTaxonomicOrder(checklist);

    const speciesSet = setById.get('species-explorer');
    if (speciesSet) {
        const seenSpecies = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const speciesKey = getObservationSpeciesKey(observation);
            if (!speciesKey || seenSpecies.has(speciesKey)) return [];

            seenSpecies.add(speciesKey);
            return [{
                value: seenSpecies.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: observation.taxonomicOrder ? speciesDetailPathByTaxonomicOrder.get(observation.taxonomicOrder) : undefined,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, speciesSet.milestones, progressEvents);
    }

    const habitatSet = setById.get('habitat-explorer');
    if (habitatSet) {
        const earliestObservationByLocation = new Map<string, {date?: string, time?: string, sortTime: number}>();

        for (const observation of sortedObservations) {
            const locationKey = getObservationLocationKey(observation);
            if (!locationKey || earliestObservationByLocation.has(locationKey)) continue;

            earliestObservationByLocation.set(locationKey, {
                date: observation.date,
                time: observation.time,
                sortTime: getDateTime(observation.date, observation.time)
            });
        }

        const habitatEvents = observationsByLocation
            .filter(hasEcoData)
            .map(locationGroup => {
                const locationKey = getLocationGroupKey(locationGroup);
                const firstObservation = earliestObservationByLocation.get(locationKey);
                const habitat = getPreferredHabitatPrimary(locationGroup.ecoData);

                return habitat && firstObservation ? {
                    habitat,
                    date: firstObservation.date,
                    sortTime: firstObservation.sortTime,
                    location: locationGroup.location,
                    locationId: locationGroup.locationId || ''
                } : undefined;
            })
            .filter((item): item is {habitat: string, date: string | undefined, sortTime: number, location: string, locationId: string} => Boolean(item))
            .sort((left, right) => left.sortTime - right.sortTime);

        const seenHabitats = new Set<string>();
        const progressEvents = habitatEvents.flatMap(event => {
            if (seenHabitats.has(event.habitat)) return [];

            seenHabitats.add(event.habitat);
            return [{
                value: seenHabitats.size,
                date: event.date,
                unlockContext: {
                    primaryLabel: event.habitat,
                    secondaryLabel: event.location,
                    secondaryDetailPath: event.locationId ? buildHotspotDetailPath(event.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, habitatSet.milestones, progressEvents);
    }

    const familySet = setById.get('family-finder');
    if (familySet) {
        const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);
        const seenFamilies = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const family = familyBySpeciesKey.get(getObservationSpeciesKey(observation));
            if (!family || seenFamilies.has(family.familyCommon)) return [];

            seenFamilies.add(family.familyCommon);
            return [{
                value: seenFamilies.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: family.familyCommon,
                    primaryDetailPath: `/family-explorer/family/${family.familySlug}`,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, familySet.milestones, progressEvents);
    }

    const countySet = setById.get('county-lister');
    if (countySet) {
        const seenCounties = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const countyKey = getCountyKey(observation);
            if (!countyKey || seenCounties.has(countyKey)) return [];

            seenCounties.add(countyKey);
            return [{
                value: seenCounties.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.county || 'Unknown county',
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, countySet.milestones, progressEvents);
    }

    const countrySet = setById.get('world-traveler');
    if (countrySet) {
        const seenCountries = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const country = getCountryCode(observation);
            if (!country || seenCountries.has(country)) return [];

            seenCountries.add(country);
            return [{
                value: seenCountries.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: country,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, countrySet.milestones, progressEvents);
    }

    const bigYearSet = setById.get('big-year-chaser');
    if (bigYearSet) {
        const speciesByYear = new Map<string, Set<string>>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const year = getYear(observation.date);
            const speciesKey = getObservationSpeciesKey(observation);
            if (!year || !speciesKey) return [];

            const seenSpeciesForYear = speciesByYear.get(year) ?? new Set<string>();
            const previousSize = seenSpeciesForYear.size;
            seenSpeciesForYear.add(speciesKey);
            speciesByYear.set(year, seenSpeciesForYear);

            if (seenSpeciesForYear.size === previousSize) return [];

            const bestYearCount = Math.max(...Array.from(speciesByYear.values()).map(species => species.size));
            return [{
                value: bestYearCount,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: observation.taxonomicOrder ? speciesDetailPathByTaxonomicOrder.get(observation.taxonomicOrder) : undefined,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, bigYearSet.milestones, progressEvents);
    }

    const photoSet = setById.get('photographer');
    if (photoSet) {
        let photoObservationCount = 0;
        const progressEvents = sortedObservations.flatMap(observation => {
            if (getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0) return [];

            photoObservationCount += 1;
            return [{
                value: photoObservationCount,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: observation.taxonomicOrder ? speciesDetailPathByTaxonomicOrder.get(observation.taxonomicOrder) : undefined,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, photoSet.milestones, progressEvents);
    }

    return milestoneDates;
}

const buildMetricSnapshot = ({rawData, lifeList, observationsByLocation, checklist}: AchievementsViewProps): AchievementMetricSnapshot => {
    const latestObservation = [...rawData].sort((left, right) => getDateTime(right.date, right.time) - getDateTime(left.date, left.time))[0];
    const latestObservationYear = latestObservation?.date ? parseInt(getYear(latestObservation.date), 10) : undefined;

    return {
        species: getUniqueSpeciesCount(lifeList),
        habitats: getUniqueHabitatCount(observationsByLocation),
        families: getSeenFamilyCount(checklist),
        counties: new Set(rawData.map(getCountyKey).filter(Boolean)).size,
        countries: new Set(rawData.map(getCountryCode).filter(Boolean)).size,
        bigYearSpecies: getBigYearSpeciesCount(rawData),
        currentYearSpecies: getCurrentYearSpeciesCount(rawData),
        photoObservations: getPhotoObservationCount(rawData),
        latestObservationDate: latestObservation?.date,
        latestObservationYear
    };
}

const getMetricValue = (metrics: AchievementMetricSnapshot, metricId: AchievementMetricId) => {
    return metrics[metricId];
}

const getCompletedSetsCount = (sets: AchievementSetSummary[]) => {
    return sets.filter(set => set.earnedCount === set.totalCount).length;
}

const buildSetSummaries = (metrics: AchievementMetricSnapshot) => {
    return ACHIEVEMENT_SET_DEFINITIONS.map((setDefinition): AchievementSetSummary => {
        const currentValue = getMetricValue(metrics, setDefinition.metricId);
        const earnedCount = setDefinition.milestones.filter(milestone => currentValue >= milestone.threshold).length;
        const totalCount = setDefinition.milestones.length;
        const nextMilestone = setDefinition.milestones.find(milestone => currentValue < milestone.threshold);

        return {
            id: setDefinition.id,
            title: setDefinition.title,
            description: setDefinition.description,
            theme: setDefinition.theme,
            color: setDefinition.color,
            icon: setDefinition.icon,
            earnedCount,
            totalCount,
            percent: totalCount ? Math.round((earnedCount / totalCount) * 100) : 0,
            nextMilestone,
            currentValue
        };
    });
}

const buildEarnedAchievements = (metrics: AchievementMetricSnapshot, milestoneDates: Map<string, MilestoneUnlockDetail>): EarnedAchievement[] => {
    return ACHIEVEMENT_SET_DEFINITIONS.flatMap(setDefinition => {
        const currentValue = getMetricValue(metrics, setDefinition.metricId);

        return setDefinition.milestones
            .filter(milestone => currentValue >= milestone.threshold)
            .map(milestone => ({
                id: milestone.id,
                setId: setDefinition.id,
                setTitle: setDefinition.title,
                title: milestone.title,
                description: milestone.description,
                rarity: milestone.rarity,
                color: setDefinition.color,
                icon: setDefinition.icon,
                points: milestone.points,
                earnedDate: milestoneDates.get(milestone.id)?.date
            }));
    }).sort((left, right) => {
        const dateDifference = getDateTime(right.earnedDate) - getDateTime(left.earnedDate);
        if (dateDifference !== 0) return dateDifference;

        return right.points - left.points;
    });
}

const buildInProgressAchievements = (metrics: AchievementMetricSnapshot): InProgressAchievement[] => {
    return ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => {
        const currentCalendarYear = DateTime.now().year;
        const currentValue = setDefinition.id === 'big-year-chaser'
            ? metrics.currentYearSpecies
            : getMetricValue(metrics, setDefinition.metricId);

        if (setDefinition.id === 'big-year-chaser' && metrics.latestObservationYear !== currentCalendarYear) {
            return undefined;
        }

        const nextMilestone = setDefinition.milestones.find(milestone => currentValue < milestone.threshold);
        if (!nextMilestone) return undefined;

        return {
            id: nextMilestone.id,
            setId: setDefinition.id,
            setTitle: setDefinition.title,
            title: nextMilestone.title,
            description: nextMilestone.description,
            color: setDefinition.color,
            icon: setDefinition.icon,
            currentValue,
            threshold: nextMilestone.threshold,
            percent: Math.max(0, Math.min(100, Math.round((currentValue / nextMilestone.threshold) * 100)))
        };
    }).filter((item): item is InProgressAchievement => Boolean(item))
        .sort((left, right) => right.percent - left.percent || right.currentValue - left.currentValue);
}

export const formatAchievementDate = (date?: string) => {
    if (!date) return 'Milestone unlocked';

    const parsed = DateTime.fromISO(date);
    return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : date;
}

export const formatAchievementValue = (value: number) => {
    return value.toLocaleString();
}

export const getAchievementSetDefinitionById = (setId: string) => {
    return ACHIEVEMENT_SET_DEFINITION_BY_ID.get(setId);
}

export const buildAchievementSetPath = (setId: string) => `/discover/achievements/${encodeURIComponent(setId)}`;

export const buildAchievementSetDetail = (setId: string, props: AchievementsViewProps): AchievementSetDetail | undefined => {
    const setDefinition = ACHIEVEMENT_SET_DEFINITION_BY_ID.get(setId);
    if (!setDefinition) return undefined;

    const metrics = buildMetricSnapshot(props);
    const setSummary = buildSetSummaries(metrics).find(set => set.id === setId);
    if (!setSummary) return undefined;

    const milestoneDetails = buildMilestoneUnlockDates(props);
    const milestones: AchievementMilestoneProgress[] = setDefinition.milestones.map(milestone => ({
        ...milestone,
        earned: setSummary.currentValue >= milestone.threshold,
        earnedDate: milestoneDetails.get(milestone.id)?.date,
        unlockContext: milestoneDetails.get(milestone.id)?.unlockContext,
        currentValue: setSummary.currentValue,
        percent: Math.max(0, Math.min(100, Math.round((setSummary.currentValue / milestone.threshold) * 100)))
    }));

    return {
        set: setSummary,
        milestones,
        totalPoints: setDefinition.milestones.reduce((total, milestone) => total + milestone.points, 0)
    };
}

export const buildAchievementDashboard = (props: AchievementsViewProps): AchievementDashboard => {
    const metrics = buildMetricSnapshot(props);
    const sets = buildSetSummaries(metrics);
    const milestoneDates = buildMilestoneUnlockDates(props);
    const earnedAchievements = buildEarnedAchievements(metrics, milestoneDates);
    const completedSets = getCompletedSetsCount(sets);
    const totalAchievements = ACHIEVEMENT_SET_DEFINITIONS.reduce((total, setDefinition) => total + setDefinition.milestones.length, 0);

    return {
        stats: {
            totalPoints: earnedAchievements.reduce((total, achievement) => total + achievement.points, 0),
            earnedAchievements: earnedAchievements.length,
            totalAchievements,
            completedSets,
            completedSetsPercent: sets.length ? Math.round((completedSets / sets.length) * 100) : 0
        },
        themeOptions: ACHIEVEMENT_THEME_LABELS,
        sets,
        recentEarned: earnedAchievements.slice(0, 6),
        inProgress: buildInProgressAchievements(metrics).slice(0, 4),
        rarityGuide: ACHIEVEMENT_RARITY_GUIDE
    };
}
